import { useEffect, useRef, useState, type CSSProperties, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { createNativeEffectRenderer } from '@/lib/video-effects/nativeEffectRenderer';
import { useHasVideoEffects, useVideoEffectDescriptors } from './VideoEffectsContext';

/** Paints the existing native decoder, preserving its audio/seek/buffering owner. */
export function NativeVideoEffects({ videoRef, style, sourceKey }: {
  videoRef: RefObject<HTMLVideoElement>; style: CSSProperties; sourceKey: string;
}) {
  const effects = useVideoEffectDescriptors();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const effectsRef = useRef(effects);
  effectsRef.current = effects;
  const drawRef = useRef<() => void>(() => {});
  const [failed, setFailed] = useState(false);
  const { t } = useTranslation();
  const active = useHasVideoEffects();
  useEffect(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!active || !video || !canvas) return;
    setFailed(false);
    const renderer = createNativeEffectRenderer(canvas);
    let cancelled = false;
    let stopped = false;
    let videoFrame: number | undefined;
    let animationFrame: number | undefined;
    const draw = () => {
      if (cancelled || stopped || video.readyState < 2 || video.seeking || !video.videoWidth) return;
      try {
        const ratio = Math.min(1, 1280 / Math.max(video.videoWidth, video.videoHeight));
        renderer.draw(video, effectsRef.current, Math.max(1, Math.round(video.videoWidth * ratio)), Math.max(1, Math.round(video.videoHeight * ratio)));
        canvas.style.visibility = 'visible';
      } catch {
        stopped = true;
        canvas.style.visibility = 'hidden';
        setFailed(true);
      }
    };
    drawRef.current = draw;
    const tick = () => {
      draw();
      if (cancelled || stopped) return;
      if (typeof video.requestVideoFrameCallback === 'function') videoFrame = video.requestVideoFrameCallback(tick);
      else if (!video.paused) animationFrame = requestAnimationFrame(tick);
    };
    const play = () => {
      if (typeof video.requestVideoFrameCallback !== 'function') {
        if (animationFrame !== undefined) cancelAnimationFrame(animationFrame);
        animationFrame = requestAnimationFrame(tick);
      }
    };
    const hide = () => { canvas.style.visibility = 'hidden'; };
    for (const event of ['loadeddata', 'seeked', 'pause']) video.addEventListener(event, draw);
    video.addEventListener('seeking', hide);
    video.addEventListener('play', play);
    tick();
    return () => {
      cancelled = true;
      drawRef.current = () => {};
      if (videoFrame !== undefined) video.cancelVideoFrameCallback(videoFrame);
      if (animationFrame !== undefined) cancelAnimationFrame(animationFrame);
      for (const event of ['loadeddata', 'seeked', 'pause']) video.removeEventListener(event, draw);
      video.removeEventListener('seeking', hide);
      video.removeEventListener('play', play);
      renderer.dispose();
    };
  }, [active, videoRef, sourceKey]);
  // A paused frame must redraw when the stack changes (settings edits, seeks
  // into a section). While playing, the frame callback already draws every
  // frame with the latest stack; drawing here too would double-draw.
  useEffect(() => { if (videoRef.current?.paused !== false) drawRef.current(); }, [effects, videoRef]);
  if (!active) return null;
  return <>
    <canvas ref={canvasRef} aria-hidden="true" data-testid="video-effects-canvas" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', ...style, pointerEvents: 'none', visibility: 'hidden' }} />
    {failed && <div role="status" style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: '#171717e6', color: 'white', padding: 8, fontSize: 14 }}>{t('videoEffects.previewUnavailable')}</div>}
  </>;
}
